import React from 'react';
import { Card } from "../../tailwind/card";
import { Badge } from "../../tailwind/badge";
import { Button } from "../../tailwind/button";
import { Phone, X, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "../../../lib/utils";

export const RecipientSelectionCard = ({
  manualNumbers,
  onManualChange,
  validationErrors,
  manualMode,
  csvActive,
  excelActive,
  onImportCsv,
  onImportExcel,
  totalCount,
  importSource,
  importedCount,
  importError,
  onClearImport
}) => {
  const disableManual = csvActive || excelActive;

  return (
    <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-sm">2</div>
          <h3 className="font-semibold text-slate-900 dark:text-white">Select Recipients</h3>
        </div>
        {totalCount > 0 && (
          <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
            {totalCount} selected
          </Badge>
        )}
      </div>

      <div className="mb-4">
        <label className="text-slate-600 dark:text-slate-400 text-sm mb-1.5 block">
          Add phone number
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <textarea
            placeholder="+91 98765 43210"
            className={`w-full pl-10 pr-10 py-3 h-20 ${disableManual ? 'opacity-50 cursor-not-allowed' : ''} bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 resize-none text-slate-900 dark:text-white`}
            value={manualNumbers}
            onChange={(e) => { if (!disableManual) { onManualChange(e.target.value); } }}
            disabled={disableManual}
            title={disableManual ? 'Clear import to enable manual input' : undefined}
          />
          {manualNumbers && (
            <button
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              onClick={() => onManualChange("")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          For multiple numbers, upload a CSV/Excel. Ensure exactly one column header contains "phone" or "number".
        </p>
        {validationErrors.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                {validationErrors.includes('multiple_numbers')
                  ? 'Only a single phone number is allowed. For multiple numbers, use CSV or Excel.'
                  : 'Number should be in this format : +91 9876543210'}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          onClick={onImportCsv}
          disabled={manualMode || excelActive}
          title={manualMode ? 'Clear manual numbers to import CSV' : excelActive ? 'Excel import active. Clear it to import CSV' : undefined}
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition-all text-left",
            (importSource === "CSV")
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
              : "border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-600",
            (manualMode || excelActive) ? "opacity-50 cursor-not-allowed" : ""
          )}
        >
          <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
            <FileSpreadsheet className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-white text-sm">Import CSV</p>
            <p className="text-xs text-slate-500">Upload .csv file</p>
          </div>
        </button>

        <button
          onClick={onImportExcel}
          disabled={manualMode || csvActive}
          title={manualMode ? 'Clear manual numbers to import Excel' : csvActive ? 'CSV import active. Clear it to import Excel' : undefined}
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition-all text-left",
            (importSource === "EXCEL")
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
              : "border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-600",
            (manualMode || csvActive) ? "opacity-50 cursor-not-allowed" : ""
          )}
        >
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-white text-sm">Import Excel Sheet</p>
            <p className="text-xs text-slate-500">Upload .xlsx or .xls file</p>
          </div>
        </button>
      </div>

      {manualMode && (
        <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>
              {(csvActive || excelActive)
                ? 'An import is active. Clear it to use manual numbers or another import method.'
                : 'Manual numbers are set. CSV/Excel import is disabled until you clear manual numbers.'}
            </span>
          </div>
        </div>
      )}

      {csvActive && (
        <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>CSV import is active. Manual input and Excel import are disabled. Clear import to enable other methods.</span>
          </div>
        </div>
      )}

      {excelActive && (
        <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Excel import is active. Manual input and CSV import are disabled. Clear import to enable other methods.</span>
          </div>
        </div>
      )}

      {importError && (
        <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{importError}</span>
          </div>
        </div>
      )}

      {importedCount > 0 && importSource && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-medium text-emerald-900 dark:text-emerald-300 text-sm">
                {importedCount} imported from {importSource}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClearImport}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
};
